"use client"

import { useLayoutEffect, useState, useEffect } from "react"
import { ExternalLink } from "lucide-react"

const ConfirmedCompanies = () => {
  const [currentAdIndex, setCurrentAdIndex] = useState(0)

  useLayoutEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0)
  }, [])

  // Enhanced sponsor data with Ethiopian Airlines prominently featured
  const sponsors = [
    {
      name: "Ethiopian Airlines",
      logo: "/assets/images/airlines.png",
      tagline: "Africa's Largest Aviation Group",
      description:
        "Official airline partner of the Africa Maritime Conference. Connecting Africa to the world with excellence in aviation services.",
      website: "https://www.ethiopianairlines.com/",
      category: "Aviation Partner",
      featured: true,
    },
    {
      name: "Ethiopian Maritime Authority",
      logo: "/assets/images/ema.png",
      tagline: "Leading Maritime Excellence",
      description: "Regulating and promoting Ethiopia's maritime industry development.",
      website: "https://etmaritime.com/",
      category: "Maritime Authority",
    },
    {
      name: "EMA Marine",
      logo: "/assets/images/EMA_logo.png",
      tagline: "Global Maritime Solutions",
      description: "Providing comprehensive maritime services and crew management.",
      website: "https://www.emacrew.com/",
      category: "Marine Services",
    },
  ]

  // Auto-rotate ads
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentAdIndex((prev) => (prev + 1) % sponsors.length)
    }, 5000)
    return () => clearInterval(interval)
  }, [sponsors.length])

  // Company data from the Google Sheets
  const confirmedCompanies = [
    {
      name: "Seaspan Crew Management",
      logo: "/assets/images/Seaspan_Marine_Corporation_logo (1).svg.webp",
      category: "Crew Management",
      description: "Leading provider of maritime crew management services worldwide.",
    },
    {
      name: "Navarone",
      logo: "/assets/images/Navarone-logo-Header.png",
      category: "Maritime Services",
      description: "Comprehensive maritime solutions and vessel management services.",
    },
    {
      name: "Uniteam Marine",
      logo: "/assets/images/Uniteam Marine Logo.png",
      category: "Ship Management",
      description: "Professional ship management and maritime consulting services.",
    },
    {
      name: "Navios Maritime",
      logo: "/assets/images/Navios-Maritime-Holdings-Inc_logo.svg",
      category: "Shipping",
      description: "International shipping company specializing in dry bulk transportation.",
    },
    {
      name: "CSM",
      logo: "/assets/images/CSM_logo.svg",
      category: "Maritime Services",
      description: "Comprehensive ship management and maritime solutions provider.",
    },
    {
      name: "Danaos",
      logo: "/assets/images/danaos-logo.svg",
      category: "Container Shipping",
      description: "Leading independent owner and operator of containerships.",
    },
    {
      name: "Star Bulk",
      logo: "/assets/images/StarBulk_Logo.png",
      category: "Dry Bulk Shipping",
      description: "Global shipping company focused on the transportation of dry bulk cargoes.",
    },
    {
      name: "Atlantic Lloyd",
      logo: "/assets/images/Atlantic.png",
      category: "Maritime Services",
      description: "Professional maritime services and vessel operations.",
    },
    {
      name: "Maersk",
      logo: "/assets/images/maeresk.png",
      category: "Global Logistics",
      description: "World's largest container shipping company and supply chain solutions provider.",
    },
    {
      name: "Overseas Ship",
      logo: "/assets/images/osg.png",
      category: "Ship Management",
      description: "International ship management and maritime services company.",
    },
    {
      name: "Rocktree",
      logo: "/assets/images/Rocktree logo.jpg",
      category: "Maritime Consulting",
      description: "Specialized maritime consulting and advisory services.",
    },
    {
      name: "Torm",
      logo: "/assets/images/torm.png",
      category: "Product Tankers",
      description: "Leading product tanker shipping company serving global markets.",
    },
    {
      name: "Yialos Manning",
      logo: "/assets/images/yialosa.png",
      category: "Crew Management",
      description: "Professional maritime crew management and manning services.",
    },
  ]

  const FeaturedSponsorBanner = ({ sponsor }) => (
    <div className="bg-gradient-to-r from-[#A65C4A] to-[#A65C4A]/80 text-white py-4 md:py-6 mb-6 md:mb-8 rounded-xl shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 md:gap-6">
            <div className="flex items-center gap-3">
              <span className="text-xs md:text-sm bg-white/20 px-2 md:px-3 py-1 rounded-full whitespace-nowrap">
                Official Partner
              </span>
              <img
                src={sponsor.logo || "/placeholder.svg"}
                alt={sponsor.name}
                className="h-10 md:h-16 w-auto max-w-[120px] md:max-w-[200px] object-contain bg-white/10 p-1 md:p-2 rounded-lg"
              />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-lg md:text-2xl font-bold truncate">{sponsor.name}</h3>
              <p className="text-sm md:text-lg opacity-90 truncate">{sponsor.tagline}</p>
              <p className="text-xs md:text-sm opacity-80 mt-1 line-clamp-2 md:line-clamp-1">{sponsor.description}</p>
            </div>
          </div>
          <a
            href={sponsor.website}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white text-[#A65C4A] px-4 md:px-6 py-2 md:py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center justify-center gap-2 text-sm md:text-base whitespace-nowrap"
          >
            Learn More <ExternalLink className="h-3 w-3 md:h-4 md:w-4" />
          </a>
        </div>
      </div>
    </div>
  )

  const SidebarAd = ({ sponsor }) => (
    <div className="bg-gradient-to-br from-white to-gray-50 border-2 border-[#A65C4A]/20 rounded-xl p-4 md:p-6 shadow-lg">
      <div className="flex items-center justify-between mb-3 md:mb-4">
        <span className="text-xs text-[#A65C4A] bg-[#A65C4A]/10 px-2 md:px-3 py-1 rounded-full font-semibold">
          {sponsor.category}
        </span>
        <span className="text-xs text-gray-500">Sponsored</span>
      </div>
      <div className="flex items-center gap-3 md:gap-4 mb-3 md:mb-4">
        <img
          src={sponsor.logo || "/placeholder.svg"}
          alt={sponsor.name}
          className="h-8 md:h-12 w-auto max-w-[80px] md:max-w-[100px] object-contain flex-shrink-0"
        />
        <div className="flex-1 min-w-0">
          <h4 className="text-base md:text-lg font-bold text-gray-800 truncate">{sponsor.name}</h4>
          <p className="text-xs md:text-sm text-[#A65C4A] font-medium truncate">{sponsor.tagline}</p>
        </div>
      </div>
      <p className="text-xs md:text-sm text-gray-600 mb-3 md:mb-4 line-clamp-2 md:line-clamp-3">
        {sponsor.description}
      </p>
      <a
        href={sponsor.website}
        target="_blank"
        rel="noopener noreferrer"
        className="w-full bg-[#A65C4A] hover:bg-[#A65C4A]/90 text-white py-2 px-3 md:px-4 rounded-lg transition-colors flex items-center justify-center gap-2 font-medium text-sm md:text-base"
      >
        Visit Website <ExternalLink className="h-3 w-3 md:h-4 md:w-4" />
      </a>
    </div>
  )

  const currentSponsor = sponsors[currentAdIndex]

  return (
    <section className="min-h-screen bg-white" style={{ backgroundColor: "rgba(255, 255, 255, 0.8)" }}>
      <section
        className="relative bg-[#0E6FA9] overflow-hidden"
        style={{ backgroundColor: "rgba(255, 255, 255, 0.8)" }}
      >
        <div className="absolute inset-0 z-0">
          <img
            src="/assets/images/contactimage.jpg"
            alt="Maritime background"
            className="absolute inset-0 w-full h-full object-cover opacity-7"
          />
        </div>
        <div className="absolute inset-0 bg-[#0E6FA9]/80"></div>
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto py-16 md:py-24">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
            Confirmed <span className="text-[#A65C4A]">Companies</span>
          </h1>
          <div className="h-1 w-16 md:w-24 bg-[#A65C4A] mx-auto mb-4 md:mb-6"></div>
          <p className="text-lg md:text-xl lg:text-2xl text-white/90 font-light mb-4 md:mb-6 px-4">
            Meet the leading maritime companies that have confirmed their attendance at the Africa Maritime Conference
          </p>
          <div className="inline-flex items-center px-3 md:px-4 py-2 bg-green-500/20 backdrop-blur-sm text-green-100 rounded-full border border-green-400/30">
            <div className="w-2 md:w-3 h-2 md:h-3 bg-green-400 rounded-full mr-2 animate-pulse"></div>
            <span className="font-semibold text-sm md:text-base">Confirmed Companies</span>
          </div>
        </div>
      </section>

      <div className="py-4 md:py-8 px-4" style={{ backgroundColor: "rgba(255, 255, 255, 0.8)" }}>
        <div className="max-w-7xl mx-auto">
          <FeaturedSponsorBanner sponsor={sponsors[0]} />
        </div>
      </div>

      <div className="py-8 md:py-16 px-4" style={{ backgroundColor: "rgba(255, 255, 255, 0.8)" }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-4 gap-6 md:gap-8">
            {/* Main Content */}
            <div className="lg:col-span-3">
              {/* Companies Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                {confirmedCompanies.map((company, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-200 overflow-hidden"
                  >
                    {/* Company Logo Section */}
                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-8 flex items-center justify-center min-h-[150px] md:min-h-[200px]">
                      <div className="w-full h-20 md:h-32 flex items-center justify-center">
                        <img
                          src={company.logo || "/placeholder.svg?height=120&width=200&query=company logo"}
                          alt={`${company.name} logo`}
                          className="max-w-full max-h-full object-contain filter drop-shadow-sm"
                          onError={(e) => {
                            e.target.src = "/generic-company-logo.png"
                          }}
                        />
                      </div>
                    </div>

                    {/* Company Info Section */}
                    <div className="p-4 md:p-6">
                      <div className="mb-3">
                        <span className="inline-block bg-[#0E6FA9]/10 text-[#0E6FA9] text-xs font-semibold px-2 md:px-3 py-1 rounded-full">
                          {company.category}
                        </span>
                      </div>
                      <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 md:mb-3 leading-tight">
                        {company.name}
                      </h3>
                      <p className="text-gray-600 text-sm leading-relaxed">{company.description}</p>

                      {/* Confirmed Status */}
                      <div className="mt-3 md:mt-4 flex items-center">
                        <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                        <span className="text-green-700 font-semibold text-sm">Attendance Confirmed</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Call to Action Section */}
              <div className="mt-12 md:mt-16 text-center bg-gradient-to-r from-[#0E6FA9] to-[#0E6FA9]/80 rounded-2xl p-6 md:p-8 text-white">
                <h2 className="text-2xl md:text-3xl font-bold mb-3 md:mb-4">Join These Industry Leaders</h2>
                <p className="text-lg md:text-xl mb-4 md:mb-6 opacity-90">
                  Don't miss the opportunity to network with Africa's top maritime companies
                </p>
                <a
                  href="/register#/register"
                  className="inline-block bg-[#A65C4A] text-white font-bold px-6 md:px-8 py-2 md:py-3 rounded-lg hover:bg-[#A65C4A]/90 transition-colors text-base md:text-lg"
                >
                  Register Now
                </a>
              </div>

              {/* Statistics Section */}
              <div className="mt-8 md:mt-12 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                <div className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-[#0E6FA9]">{confirmedCompanies.length}</div>
                  <div className="text-gray-600 font-medium text-sm md:text-base">Confirmed Companies</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-[#A65C4A]">8</div>
                  <div className="text-gray-600 font-medium text-sm md:text-base">Industry Categories</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-[#0E6FA9]">15+</div>
                  <div className="text-gray-600 font-medium text-sm md:text-base">Countries Represented</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-[#A65C4A]">500+</div>
                  <div className="text-gray-600 font-medium text-sm md:text-base">Expected Attendees</div>
                </div>
              </div>
            </div>

            <div className="hidden lg:block lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                <SidebarAd sponsor={currentSponsor} />

                {/* Additional static sponsor */}
                <SidebarAd sponsor={sponsors[1]} />

                {/* Conference info card */}
                <div className="bg-gradient-to-br from-[#0E6FA9] to-[#0E6FA9]/80 text-white rounded-xl p-6 shadow-lg">
                  <h3 className="text-lg font-bold mb-3">Conference Details</h3>
                  <div className="space-y-2 text-sm">
                    <p>
                      <strong>Date:</strong> March 15-17, 2024
                    </p>
                    <p>
                      <strong>Location:</strong> Addis Ababa, Ethiopia
                    </p>
                    <p>
                      <strong>Expected Attendees:</strong> 500+
                    </p>
                  </div>
                  <a
                    href="/register"
                    className="mt-4 w-full bg-[#A65C4A] hover:bg-[#A65C4A]/90 text-white py-2 px-4 rounded-lg transition-colors flex items-center justify-center font-medium"
                  >
                    Register Now
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:hidden mt-8 space-y-4">
            <h3 className="text-xl font-bold text-center text-gray-800 mb-4">Our Sponsors</h3>
            <SidebarAd sponsor={currentSponsor} />
            <SidebarAd sponsor={sponsors[1]} />
          </div>
        </div>
      </div>
    </section>
  )
}

export default ConfirmedCompanies
